import { inject, injectable, interfaces } from 'inversify';
import { ACDLError, CUSTOM_ERROR_CODES } from '../../../__legacy__/models/error.model';
import { IApiService } from '../../../__legacy__/services/api.service.base';
import { IApiServiceTraining } from '../../../__legacy__/services/api.service.training';
import { IInferenceStartDto, IJobActionDto, IJobDownloadTokenDto, ITrainingStartDto } from '../jobs.api.model';
import { IValidJobType, JOB_DOWNLOAD_TYPE, JOB_TYPE } from '../jobs.model';
import { getJobApiPrefix } from './utils';

export const IMPORT_ERROR_CODES = [CUSTOM_ERROR_CODES.NO_TRAIN_DATASETS, CUSTOM_ERROR_CODES.MISSING_FILES];

export interface IJobActionsApiService {
  postJobStartAsync: <T extends IValidJobType>(
    workspaceId: string,
    jobType: T,
    request: T extends JOB_TYPE.INFERENCE ? IInferenceStartDto : ITrainingStartDto,
    params: T extends JOB_TYPE.INFERENCE ? { training_job_id: string } : { auto_training: boolean }
  ) => Promise<IJobActionDto | ACDLError>;
  postJobRestartAsync: <T extends IValidJobType>(
    workspaceId: string,
    jobId: string,
    jobType: T,
    request?: T extends JOB_TYPE.INFERENCE ? IInferenceStartDto : ITrainingStartDto
  ) => Promise<IJobActionDto | ACDLError>;
  postJobStopAsync: (workspaceId: string, jobId: string, jobType: IValidJobType) => Promise<string | ACDLError>;
  deleteJobAsync: (workspaceId: string, jobId: string, jobType: IValidJobType) => Promise<string | ACDLError>;
  getJobDownloadToken: (workspaceId: string, jobId: string, jobType: IValidJobType) => Promise<IJobDownloadTokenDto | ACDLError>;
  getJobDownloadUrls: (workspaceId: string, jobId: string, jobType: IValidJobType) => Record<JOB_DOWNLOAD_TYPE, string>;
  postJobImportAsync: (workspaceId: string, formData: FormData) => Promise<IJobActionDto | ACDLError>;
}

export namespace IJobActionsApiService {
  export const $: interfaces.ServiceIdentifier<IJobActionsApiService> = Symbol('JOBS_API_SERVICE');
}

@injectable()
export class JobActionsApiService implements IJobActionsApiService {
  constructor(@inject(IApiServiceTraining.$) private readonly apiTrainingService: IApiService) {}

  postJobStartAsync = async <T extends IValidJobType>(
    workspaceId: string,
    jobType: T,
    request: T extends JOB_TYPE.INFERENCE ? IInferenceStartDto : ITrainingStartDto,
    params: T extends JOB_TYPE.INFERENCE ? { training_job_id: string } : { auto_training: boolean }
  ): Promise<IJobActionDto | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/start`, request, { params });
  };

  postJobRestartAsync = async <T extends IValidJobType>(
    workspaceId: string,
    jobId: string,
    jobType: T,
    request?: T extends JOB_TYPE.INFERENCE ? IInferenceStartDto : ITrainingStartDto
  ): Promise<IJobActionDto | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/start`, request);
  };

  postJobStopAsync = async (workspaceId: string, jobId: string, jobType: IValidJobType): Promise<string | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/stop`);
  };

  deleteJobAsync = async (workspaceId: string, jobId: string, jobType: IValidJobType): Promise<string | ACDLError> => {
    return this.apiTrainingService.deleteAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}`);
  };

  getJobDownloadToken = async (workspaceId: string, jobId: string, jobType: IValidJobType): Promise<IJobDownloadTokenDto | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/token`);
  };

  getJobDownloadUrls(workspaceId: string, jobId: string, jobType: IValidJobType): Record<JOB_DOWNLOAD_TYPE, string> {
    return {
      [JOB_DOWNLOAD_TYPE.MODEL]: this.apiTrainingService.getUrl(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/model`),
      [JOB_DOWNLOAD_TYPE.RESULTS]: this.apiTrainingService.getUrl(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/artifacts`),
    };
  }

  postJobImportAsync = async (workspaceId: string, formData: FormData): Promise<IJobActionDto | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/training-jobs/model`, formData);
  };
}
